import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../../shared/theme';
import Avatar from '../../components/ui/Avatar';

const BASE = process.env.REACT_APP_API_URL || '/api';

function getSupportedMimeType() {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  if (typeof MediaRecorder === 'undefined') return '';
  return types.find(t => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || '';
}

function formatDur(s) {
  const sec = Math.floor(s);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/**
 * Compact inline voice-chat button for game header.
 * Hold to record, release to send. Tap to toggle incoming message panel.
 */
function GameVoiceChat({ partner, currentUserId, token, myRole, lang }) {
  const nl = lang === 'nl';

  const [recording,       setRecording]       = useState(false);
  const [elapsed,         setElapsed]         = useState(0);
  const [sending,         setSending]         = useState(false);
  const [sent,            setSent]            = useState(false);
  const [error,           setError]           = useState(null);
  const [incoming,        setIncoming]        = useState(null);
  const [playing,         setPlaying]         = useState(false);
  const [panelOpen,       setPanelOpen]       = useState(false);
  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);

  const mrRef      = useRef(null);
  const chunksRef  = useRef([]);
  const timerRef   = useRef(null);
  const elapsedRef = useRef(0);
  const audioRef   = useRef(null);
  const lastTsRef  = useRef(null);
  const pollRef    = useRef(null);
  const wrapRef    = useRef(null); // for popup positioning

  const partnerCol = myRole === 'inviter' ? 'voiceB' : 'voiceA';

  // ── Poll game session for partner's voice message ─────────────────────────
  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BASE}/game-sessions/${token}`);
      if (!res.ok) return;
      const data = await res.json();
      const msg = data[partnerCol];
      if (!msg || !msg.audio) return;
      if (msg.ts === lastTsRef.current) return;
      lastTsRef.current = msg.ts;
      setIncoming(msg);
      setPanelOpen(true);
      const a = new Audio(msg.audio);
      audioRef.current = a;
      a.onplay  = () => setPlaying(true);
      a.onended = () => setPlaying(false);
      a.play().then(() => setAutoPlayBlocked(false)).catch(() => setAutoPlayBlocked(true));
    } catch {}
  }, [token, partnerCol]);

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [poll]);

  // ── Recording ─────────────────────────────────────────────────────────────
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(100);
      mrRef.current = mr;
      elapsedRef.current = 0;
      setElapsed(0);
      setRecording(true);
      setSent(false);
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
        if (elapsedRef.current >= 60) stopRec();
      }, 1000);
    } catch {
      setError(nl ? 'Microfoon geweigerd' : 'Mic access denied');
      setTimeout(() => setError(null), 3000);
    }
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    const mr = mrRef.current;
    if (!mr || mr.state === 'inactive') return;
    const duration = elapsedRef.current;
    mr.onstop = () => {
      const mimeType = mr.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      mr.stream.getTracks().forEach(t => t.stop());
      if (blob.size < 500) return;
      const reader = new FileReader();
      reader.onload = async () => {
        setSending(true);
        try {
          await fetch(`${BASE}/game-sessions/${token}/voice`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              role: myRole,
              voice: { audio: reader.result, duration, ts: Date.now() },
            }),
          });
          setSent(true);
          setTimeout(() => setSent(false), 2000);
        } catch {}
        setSending(false);
      };
      reader.readAsDataURL(blob);
    };
    mr.stop();
    setRecording(false);
  };

  const playIncoming = () => {
    if (!incoming?.audio) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    const a = new Audio(incoming.audio);
    audioRef.current = a;
    a.onplay  = () => { setPlaying(true); setAutoPlayBlocked(false); };
    a.onended = () => setPlaying(false);
    a.play().catch(() => {});
  };

  const hasUnread = incoming && (!panelOpen || autoPlayBlocked);

  // ── Render ────────────────────────────────────────────────────────────────
  const popup = panelOpen && (
    <div style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 200,
      background: _T.bg2, border: `1px solid ${_T.cardBorderTop}`,
      borderRadius: 16, padding: 12, minWidth: 200, maxWidth: 240,
      boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
    }}>
      {/* Incoming message */}
      {incoming && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Avatar user={partner} size={20} profileImage={partner.picture || null} />
            <span style={{ color: C.silver, fontSize: 11 }}>{partner.name}</span>
            <button onClick={() => setPanelOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 14 }}>×</button>
          </div>
          <button onClick={playIncoming} style={{ display: 'flex', alignItems: 'center', gap: 8, background: `${_T.accent}18`, border: `1px solid ${_T.accent}44`, borderRadius: 10, padding: '7px 10px', cursor: 'pointer', width: '100%', marginBottom: 6 }}>
            <span style={{ fontSize: 16 }}>{playing ? '⏸' : '▶️'}</span>
            <div style={{ flex: 1, height: 3, background: `${_T.accent}33`, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: playing ? '100%' : '0%', background: _T.accent, transition: playing ? `width ${incoming.duration || 5}s linear` : 'none', borderRadius: 2 }} />
            </div>
            <span style={{ color: C.silver, fontSize: 10 }}>{formatDur(incoming.duration || 0)}</span>
          </button>
          {autoPlayBlocked && <div style={{ fontSize: 10, color: C.orange, textAlign: 'center', marginBottom: 6 }}>{nl ? '▶ Tik om te beluisteren' : '▶ Tap to listen'}</div>}
        </>
      )}

      {/* Status */}
      {(recording || sending || sent || error) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
          {recording && <>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', animation: 'vcPulse 1s infinite', flexShrink: 0 }} />
            <span style={{ color: C.white, fontSize: 11 }}>{formatDur(elapsed)}</span>
            <span style={{ color: C.silver, fontSize: 10 }}>{nl ? 'Opnemen...' : 'Recording...'}</span>
          </>}
          {sending && <span style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Versturen...' : 'Sending...'}</span>}
          {sent    && <span style={{ color: '#22c55e', fontSize: 11 }}>✓ {nl ? 'Verzonden!' : 'Sent!'}</span>}
          {error   && <span style={{ color: '#ef4444', fontSize: 11 }}>{error}</span>}
        </div>
      )}

      {/* Hint */}
      {!recording && !incoming && !sending && !sent && !error && (
        <div style={{ color: `${C.silver}99`, fontSize: 10, textAlign: 'center' }}>
          {nl ? 'Houd de mic vast om op te nemen' : 'Hold mic to record'}
        </div>
      )}

      <style>{`
        @keyframes vcPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(1.2)} }
      `}</style>
    </div>
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {/* Compact mic button */}
      <div style={{ position: 'relative' }}>
        <button
          onMouseDown={startRec}
          onMouseUp={stopRec}
          onMouseLeave={recording ? stopRec : undefined}
          onTouchStart={e => { e.preventDefault(); startRec(); }}
          onTouchEnd={e => { e.preventDefault(); stopRec(); }}
          onClick={() => { if (!recording) setPanelOpen(p => !p); }}
          disabled={sending}
          title={nl ? 'Houd vast om op te nemen' : 'Hold to record'}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: recording ? '#ef4444' : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
            border: recording ? '2px solid #fca5a5' : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 16,
            boxShadow: recording ? '0 0 0 4px #ef444422' : '0 2px 8px rgba(0,0,0,0.4)',
            animation: recording ? 'vcPulse 1s infinite' : 'none',
            WebkitTapHighlightColor: 'transparent',
            flexShrink: 0,
          }}
        >
          🎤
        </button>
        {/* Unread badge */}
        {hasUnread && (
          <span style={{
            position: 'absolute', top: -3, right: -3,
            width: 12, height: 12, borderRadius: '50%',
            background: '#ef4444', border: '2px solid #0a1220',
            animation: 'vcPulse 1s infinite',
          }} />
        )}
      </div>

      {/* Partner chip */}
      <div
        onClick={() => setPanelOpen(p => !p)}
        style={{ display: 'flex', alignItems: 'center', gap: 5, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 20, padding: '3px 8px 3px 5px', cursor: 'pointer' }}
      >
        <Avatar user={partner} size={20} profileImage={partner.picture || null} />
        <span style={{ color: C.silver, fontSize: 11, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{partner.name}</span>
      </div>

      {/* Dropdown popup — rendered into portal so it's never clipped */}
      {ReactDOM.createPortal(
        panelOpen ? (
          <>
            {/* Click-outside overlay */}
            <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setPanelOpen(false)} />
            <div style={{ position: 'fixed', zIndex: 200, ...getPopupPos(wrapRef) }}>
              {popup}
            </div>
          </>
        ) : null,
        document.body
      )}
    </div>
  );
}

/** Calculate popup position relative to the trigger element */
function getPopupPos(ref) {
  if (!ref.current) return { top: 60, right: 16 };
  const r = ref.current.getBoundingClientRect();
  return { top: r.bottom + 8, right: window.innerWidth - r.right };
}

export default GameVoiceChat;
